import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Popover,
  AutoComplete,
} from "antd";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import WeldingSystemCheckbox from "./WeldingSystemCheckbox";
import { inputTypes } from "../../constants/commonConstants";
import {
  projectCreationForm,
  createProjectFormConfig,
} from "../../helpers/createProject/project-creation";

const ProjectDetails = ({
  form,
  projectDetails,
  handleProjectDetailsChange,
  weldTagging,
  handleWeldTaggingChange,
  weldingSystem,
  handleWeldingSystemChange,
  numHeads,
  handleNumHeadsChange,
  numStations,
  handleNumStationsChange,
  isEditMode,
}) => {
  const { t } = useTranslation();
  const projects =
    useSelector(
      (state) => state.data360LiveProjectReducer?.projectDetails?.projects
    ) || [];
  const currentProject =
    useSelector(
      (state) => state.data360LiveProjectReducer?.currentProject
    ) || null;

  // Get dynamic form configuration - only pass currentProject in edit mode
  const dynamicFormConfig = createProjectFormConfig(projects, isEditMode ? currentProject : null);
  const formConfig = { ...projectCreationForm, ...dynamicFormConfig };

  const renderFormItem = (fieldName) => {
    const { type, label, placeholder, rules, options, tooltip, shouldRender, unitSuffix } =
      formConfig[fieldName];

    // Check if field should be rendered
    if (shouldRender && !shouldRender(form)) {
      return null;
    }

    // Determine specific class name based on field name
    let specificClassName = "form-group";
    if (fieldName === "weldTagging") specificClassName += " weld-tagging-group";
    if (fieldName === "numHeads") specificClassName += " heads-group";
    if (fieldName === "numStations") specificClassName += " stations-group";

    // Function to disable dates before start date for end date field
    const disabledDate = (current) => {
      if (fieldName === "endDate") {
        const startDate = form.getFieldValue("startDate");
        return startDate
          ? current && current < startDate.startOf("day")
          : false;
      }
      return false;
    };

    switch (type) {
      case inputTypes.input:
        return (
          <Form.Item
            name={fieldName}
            className={specificClassName}
            label={
              <span>
                <Popover
                  content={t(tooltip)}
                  placement="topLeft"
                  trigger="hover"
                >
                  {t(label)}{unitSuffix || ''}
                </Popover>
              </span>
            }
            rules={rules}
          >
            <Input
              placeholder={t(placeholder)}
              onChange={(e) =>
                handleProjectDetailsChange(fieldName, e.target.value)
              }
            />
          </Form.Item>
        );

      case inputTypes.select:
        return (
          <Form.Item
            name={fieldName}
            className={specificClassName}
            label={
              <span>
                <Popover
                  content={t(tooltip)}
                  placement="topLeft"
                  trigger="hover"
                >
                  {t(label)}{unitSuffix || ''}
                </Popover>
              </span>
            }
            rules={rules}
          >
            <Select
              placeholder={t(placeholder)}
              onChange={(value) => handleProjectDetailsChange(fieldName, value)}
            >
              {options.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        );

      case inputTypes.editableSelect:
        return (
          <Form.Item
            name={fieldName}
            className={specificClassName}
            label={
              <span>
                <Popover
                  content={t(tooltip)}
                  placement="topLeft"
                  trigger="hover"
                >
                  {t(label)}{unitSuffix || ''}
                </Popover>
              </span>
            }
            rules={rules}
          >
            <AutoComplete
              options={options.map((opt) => ({ value: opt }))}
              // placeholder={t(placeholder)}
              onChange={(value) => handleProjectDetailsChange(fieldName, value)}
              allowClear
            />
          </Form.Item>
        );

      case inputTypes.date:
        return (
          <Form.Item
            name={fieldName}
            className={specificClassName}
            label={
              <span>
                <Popover
                  content={t(tooltip)}
                  placement="topLeft"
                  trigger="hover"
                >
                  {t(label)}{unitSuffix || ''}
                </Popover>
              </span>
            }
            rules={rules}
          >
            <DatePicker
              style={{ width: "100%" }}
              onChange={(date) => handleProjectDetailsChange(fieldName, date)}
              disabledDate={disabledDate}
            />
          </Form.Item>
        );

      case inputTypes.radio:
        return (
          <Form.Item
            name={fieldName}
            className={specificClassName}
            label={
              <span>
                <Popover
                  content={t(tooltip)}
                  placement="topLeft"
                  trigger="hover"
                >
                  {t(label)}{unitSuffix || ''}
                </Popover>
              </span>
            }
            rules={rules}
          >
            <div className="radio-group">
              <Radio.Group
                onChange={handleWeldTaggingChange}
                value={weldTagging}
              >
                {options.map((option) => (
                  <Radio
                    key={option.value}
                    value={option.value}
                    className="radio-button"
                  >
                    {option.label}
                  </Radio>
                ))}
              </Radio.Group>
            </div>
          </Form.Item>
        );

      case inputTypes.number:
        return (
          <Form.Item
            name={fieldName}
            className={specificClassName}
            label={
              <span>
                <Popover
                  content={t(tooltip)}
                  placement="topLeft"
                  trigger="hover"
                >
                  {t(label)}{unitSuffix || ''}
                </Popover>
              </span>
            }
            rules={rules}
          >
            <div className="number-buttons">
              {options.map((num) => (
                <Button
                  key={num}
                  type={
                    fieldName === "numHeads"
                      ? numHeads === num.toString()
                        ? "primary"
                        : "default"
                      : numStations === num.toString()
                      ? "primary"
                      : "default"
                  }
                  shape="circle"
                  onClick={() =>
                    fieldName === "numHeads"
                      ? handleNumHeadsChange(num.toString())
                      : handleNumStationsChange(num.toString())
                  }
                >
                  {num}
                </Button>
              ))}
            </div>
          </Form.Item>
        );

      default:
        return null;
    }
  };

  // Set initial values for form fields
  React.useEffect(() => {
    form.setFieldsValue({
      ...projectDetails,
      weldTagging,
      numHeads,
      numStations,
    });
  }, [form, projectDetails, weldTagging, numHeads, numStations]);

  return (
    <div className="project-form">
      <Form form={form} layout="vertical" className="project-details-form">
        <Row gutter={[16, 16]} className="responsive-form-row">
          {/* Project Name */}
          <Col xs={24} sm={12} md={8} lg={6}>
            {renderFormItem("projectName")}
          </Col>

          {/* Project Number */}
          <Col xs={24} sm={12} md={8} lg={6}>
            {renderFormItem("projectNumber")}
          </Col>

          {/* Client Name */}
          <Col xs={24} sm={12} md={8} lg={6}>
            {renderFormItem("clientName")}
          </Col>

          {/* Technician Name */}
          <Col xs={24} sm={12} md={8} lg={6}>
            {renderFormItem("technicianName")}
          </Col>

          {/* Welds Planned */}
          <Col xs={24} sm={12} md={8} lg={6}>
            {renderFormItem("weldsPlanned")}
          </Col>

          {/* Wall Thickness */}
          <Col xs={24} sm={12} md={8} lg={6}>
            {renderFormItem("wallThickness")}
          </Col>

          {/* Pipe Diameter */}
          <Col xs={24} sm={12} md={8} lg={6}>
            {renderFormItem("pipeDiameter")}
          </Col>

          {/* Pipe Material */}
          <Col xs={24} sm={12} md={8} lg={6}>
            {renderFormItem("pipeMaterial")}
          </Col>

          {/* Start Date */}
          <Col xs={24} sm={12} md={8} lg={6}>
            {renderFormItem("startDate")}
          </Col>

          {/* End Date */}
          <Col xs={24} sm={12} md={8} lg={6}>
            {renderFormItem("endDate")}
          </Col>

          {/* Weld Tagging and Grouping */}
          <Col xs={24} sm={24} md={12} lg={8}>
            {renderFormItem("weldTagging")}
          </Col>

          {/* Welding System */}
          <Col xs={24} sm={24} md={12} lg={8}>
            <WeldingSystemCheckbox
              weldingSystem={weldingSystem}
              handleWeldingSystemChange={handleWeldingSystemChange}
              weldTagging={weldTagging}
            />
          </Col>

          {/* Number of Heads for IWM */}
          {weldingSystem.iwm && (
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="numHeads"
                label="Number of Heads for IWM"
                className="form-group heads-group"
              >
                <div className="number-buttons">
                  {[1, 2, 3, 4, 5].map((num) => (
                    <Button
                      key={num}
                      type={numHeads === num.toString() ? "primary" : "default"}
                      shape="circle"
                      onClick={() => handleNumHeadsChange(num.toString())}
                    >
                      {num}
                    </Button>
                  ))}
                </div>
              </Form.Item>
            </Col>
          )}

          {/* Number of Stations */}
          <Col xs={24} sm={12} md={12} lg={12}>
            {renderFormItem("numStations")}
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default ProjectDetails;
